"use client";

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  XAxis,
  YAxis,
} from "recharts";
import { ChartConfig, ChartContainer } from "@/components/ui/chart";
import { Card, CardContent } from "@/components/ui/card";
import { GradeDistribution } from "@/types/grading";

const chartConfig = {
  count: {
    label: "Count",
    color: "hsl(240 4.8% 95.9%)", // zinc-100
  },
  highlight: {
    label: "Current Grade",
    color: "#ff5430", // orange-500
  },
} satisfies ChartConfig;

// Custom label component to render grade labels inside bars
const GradeLabel = (props: {
  x?: string | number;
  y?: string | number;
  width?: string | number;
  height?: string | number;
  value?: string | number | null;
  currentGrade?: string | null;
}) => {
  const { x, y, width, height, value, currentGrade } = props;
  if (
    x === undefined ||
    y === undefined ||
    width === undefined ||
    value === undefined
  )
    return null;

  const xNum = typeof x === "string" ? parseFloat(x) : x;
  const yNum = typeof y === "string" ? parseFloat(y) : y;
  const heightNum =
    typeof height === "string" ? parseFloat(height) : height ?? 50;
  const valueStr = typeof value === "number" ? String(value) : value;

  if (isNaN(xNum) || isNaN(yNum)) return null;

  // Center vertically: y is top of bar, so y + height/2 centers it
  const centerY = yNum + heightNum / 2;

  return (
    <text
      x={xNum + 8}
      y={centerY}
      fill={valueStr === currentGrade ? "white" : "currentColor"}
      fontSize={14}
      fontWeight={600}
      dominantBaseline="middle"
      className="font-semibold text-xs"
    >
      {valueStr}
    </text>
  );
};

export const GradeDistributionChart = ({
  data,
  currentGrade,
}: {
  data: GradeDistribution;
  currentGrade?: string | null;
}) => {
  const chartData = data.distribution.map((entry) => ({
    grade: entry.grade,
    count: entry.count,
  }));

  return (
    <Card>
      <CardContent>
        <ChartContainer config={chartConfig} className="w-full">
          <BarChart
            accessibilityLayer
            data={chartData}
            layout="vertical"
            margin={{
              left: 0,
              right: 40,
            }}
          >
            <CartesianGrid horizontal={false} stroke="#e5e7eb" />
            <XAxis type="number" dataKey="count" hide />
            <YAxis dataKey="grade" type="category" tickMargin={10} hide />
            <Bar dataKey="count" radius={8} maxBarSize={30}>
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    entry.grade === currentGrade
                      ? "var(--color-highlight)"
                      : "var(--color-count)"
                  }
                />
              ))}
              <LabelList
                dataKey="grade"
                content={(props) => (
                  <GradeLabel
                    {...(props as {
                      x?: string | number;
                      y?: string | number;
                      width?: string | number;
                      height?: string | number;
                      value?: string | number | null;
                    })}
                    currentGrade={currentGrade}
                  />
                )}
              />
              <LabelList
                dataKey="count"
                position="right"
                className="fill-foreground"
                fontSize={12}
              />
            </Bar>
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};
